import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IdleTimer } from 'shared-components/lib/components/pages/modals/idle-timer';
import SpinnerModal from 'shared-components/lib/components/molecules/SpinnerModal';
import { ISessionContext, useSession } from 'shared-components/lib/authentication';
import { Box } from '@chakra-ui/react';
import * as authActions from './store/auth/actions';
import * as uiActions from './store/ui/actions';
import { ApplicationState } from './store';
import { CustomRoutes } from './routes';
import { setSagaContext } from './store/configureStore';
import { useLocation } from 'react-router-dom';
import isTesting from './utilities/isTesting';

function ContextWrapper() {
  const dispatch = useDispatch();
  const location = useLocation();
  const student = useSelector((state: ApplicationState) => state.student);
  const config = useSelector((state: ApplicationState) => state.config);
  const offers = useSelector((state: ApplicationState) => state.offers);
  const auth = useSelector((state: ApplicationState) => state.auth);

  const isContextLoading = useSelector((state: ApplicationState) => state.ui.isContextLoading ?? true);
  const isLoggedIn = useSelector((state: ApplicationState) => state.auth.isLoggedIn);
  const isStudentLoaded = useSelector((state: ApplicationState) => state.data.isStudentLoaded);
  const studentOffers = useSelector((state: ApplicationState) => state.offers.offers);

  const setContextLoading = useCallback(
    (isLoading: boolean) => dispatch(uiActions.setContextLoading(isLoading)),
    [dispatch]
  );
  const setContextSuccess = useCallback(() => dispatch(uiActions.setContextSuccess()), [dispatch]);
  const authSuccess = useCallback(
    (sessionContext: Omit<ISessionContext, 'getIdToken' | 'onLogout'>) =>
      dispatch(authActions.authSuccess(sessionContext)),
    [dispatch]
  );

  const session = useSession();
  const { isSessionValid } = session;

  useEffect(() => {
    const w = window as any;
    if (w.Appcues) {
      if (isLoggedIn && location.pathname === '/') {
        w.Appcues.page();
      }
    }
  }, [location, isLoggedIn]);

  useEffect(() => {
    setContextLoading(true);

    if (isSessionValid) {
      let sessionContext = { ...session };
      authSuccess(sessionContext);

      setSagaContext(session); // see configureStore
      setContextSuccess();
    } else {
      setContextLoading(false);
    }
  }, [isSessionValid]);

  const onLogout = async () => {
    await session.onLogout(true);
    dispatch(authActions.logout());
  };

  // Vite specific env var's cannot be in shared-comps used by Nextjs
  const timeout = Number(import.meta?.env?.VITE_TIMEOUT ?? 55 * 60 * 1000);
  const promptBeforeIdle = Number(import.meta?.env?.VITE_PROMPT_BEFORE_IDLE ?? 2 * 60 * 1000);
  // the loading should be skipped if student has one offer and data is loaded or student has multiple offers (skip to show offer selection page)
  const isStudentDataLoaded =
    (studentOffers?.length === 1 && isStudentLoaded) || studentOffers?.length > 1 || studentOffers?.length === 0;
  // checking offerId exists to make sure data is loaded
  // wait until student data loaded
  if (isContextLoading || !session?.isSessionValid || !isStudentDataLoaded) {
    return (
      <Box bgColor="chakra-body-bg" width="100vw" height="100vh" position="absolute">
        <SpinnerModal show={true} />
      </Box>
    );
  }

  return (
    <IdleTimer onLogout={onLogout} timeout={timeout} promptBeforeIdle={promptBeforeIdle}>
      <>
        <CustomRoutes student={student} config={config} offers={offers} auth={auth} location={location} />
        {isTesting && <div id="content-loaded" />}
      </>
    </IdleTimer>
  );
}

export { ContextWrapper };
